import React, { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import config from "../../Config";
import TransportMenu from "./TransportMenu";
import Spinner from "react-bootstrap/Spinner";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { scrollToTop } from "../Shared/ScrollOnTop";

/*
 * Bus Financial Audit - every movement of money against a bus, in date order, with a
 * running balance. This is the report the standalone transport system existed to produce,
 * rebuilt on our own data.
 *
 * The difference that matters is the dating. That system had no record of when anything
 * was paid, so it stamped the whole of a bus's revenue as a single line dated TODAY -
 * "Student Fee Collection (36 Students)" - which made the balance column a fiction. Here
 * each receipt sits on the day it was received, so the balance is a real sequence.
 */
function BusAuditReport() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const UserName = localStorage.getItem("UserName");
  const MemberType = localStorage.getItem("MemberType");

  const [loading, setLoading] = useState(true);
  const [lines, setLines] = useState([]);
  const [totals, setTotals] = useState({ income: 0, expense: 0, net: 0 });
  const [vehicles, setVehicles] = useState([]);

  const [fromDate, setFromDate] = useState(moment().startOf("year").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [vehicleId, setVehicleId] = useState("");

  const auth = useCallback(
    (extra) => ({
      AccessKey,
      UserID,
      UserName,
      MemberType: MemberType || "Admin",
      SchoolID: 1,
      ...extra,
    }),
    [AccessKey, UserID, UserName, MemberType]
  );

  const post = useCallback(
    (url, data) =>
      axios({
        method: "post",
        url: config.base_url + url,
        headers: { "Content-Type": "application/json" },
        data,
      }),
    []
  );

  const load = useCallback(() => {
    setLoading(true);
    post(
      "TransportReport/GetVehicleLedger",
      auth({
        Vehicle_ID: vehicleId ? parseInt(vehicleId, 10) : 0,
        From_Date: fromDate,
        To_Date: toDate,
      })
    )
      .then((res) => {
        if (res.data.status_code === 1) {
          setLines(res.data.ListofRecords || []);
          setTotals({
            income: res.data.TotalIncome || 0,
            expense: res.data.TotalExpense || 0,
            net: res.data.NetBalance || 0,
          });
        } else {
          setLines([]);
          setTotals({ income: 0, expense: 0, net: 0 });
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() =>
        toast.error("Network error", { position: toast.POSITION.TOP_RIGHT })
      )
      .finally(() => setLoading(false));
  }, [post, auth, vehicleId, fromDate, toDate]);

  useEffect(() => {
    document.title = "Bus Financial Audit";
    scrollToTop();
    post("Vehicle/GetVehicles", auth({}))
      .then((res) => {
        if (res.data.status_code === 1) setVehicles(res.data.ListofRecords || []);
      })
      .catch(() => setVehicles([]));
  }, [post, auth]);

  useEffect(() => {
    load();
  }, [load]);

  const money = (n) =>
    n === null || n === undefined
      ? "—"
      : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

  const typeBadge = (t) => {
    const cls =
      t === "Revenue"
        ? "bg-success text-white"
        : t === "Fuel"
        ? "bg-secondary text-white"
        : t === "Maintenance"
        ? "bg-warning text-dark"
        : "bg-info text-dark";
    return <span className={`badge ${cls}`}>{t}</span>;
  };

  const printUrl = () =>
    `/Transport/PrintBusAudit?from=${fromDate}&to=${toDate}&vehicle=${vehicleId || ""}`;

  const renderRows = () => {
    if (lines.length === 0) {
      return (
        <tr>
          <td colSpan="7" className="text-center text-muted py-4">
            No money moved against this selection in the period
          </td>
        </tr>
      );
    }

    const out = [];
    let lastVehicle = null;

    lines.forEach((l, i) => {
      // A heading whenever the bus changes, because the balance restarts there.
      if (l.Vehicle_ID !== lastVehicle) {
        lastVehicle = l.Vehicle_ID;
        out.push(
          <tr key={`h-${l.Vehicle_ID}`} className="table-active">
            <th colSpan="7" className="py-2">
              Bus No: {l.Registration_No}
            </th>
          </tr>
        );
      }

      out.push(
        <tr key={`${l.Vehicle_ID}-${i}`}>
          <td>{i + 1}</td>
          <td className="text-nowrap">{moment(l.Line_Date).format("DD-MMM-YYYY")}</td>
          <td>{typeBadge(l.Line_Type)}</td>
          <td>{l.Description}</td>
          <td className="text-end text-success">
            {l.Income > 0 ? money(l.Income) : "—"}
          </td>
          <td className="text-end text-danger">
            {l.Expense > 0 ? money(l.Expense) : "—"}
          </td>
          <td className="text-end fw-bold">
            <span className={l.Balance < 0 ? "text-danger" : ""}>{money(l.Balance)}</span>
          </td>
        </tr>
      );
    });

    return out;
  };

  return (
    <>
      <TransportMenu />
      <ToastContainer />

      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-7">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/Transport/TransportDashboard">Transport</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Bus Financial Audit
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Bus Financial Audit</div>
                <p className="text-muted small mb-0">
                  Every fee receipt, filling, repair and part issued, in the order it
                  happened. Each receipt is dated the day the money came in.
                </p>
              </div>
              <div className="col-md-5">
                <div className="page-right-actions">
                  <a
                    href={printUrl()}
                    target="_blank"
                    rel="noreferrer"
                    className="button button-white button-icon"
                  >
                    Print Report
                    <i className="ri-printer-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Income</div>
                    <div className="h4 mb-0 text-success">Rs. {money(totals.income)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Expense</div>
                    <div className="h4 mb-0 text-danger">Rs. {money(totals.expense)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Net</div>
                    <div
                      className={`h4 mb-0 ${
                        totals.net < 0 ? "text-danger" : "text-success"
                      }`}
                    >
                      Rs. {money(totals.net)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="row g-2 mb-3 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label small mb-1">From date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-1">To date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-1">Vehicle</label>
                    <select
                      className="form-select"
                      value={vehicleId}
                      onChange={(e) => setVehicleId(e.target.value)}
                    >
                      <option value="">All vehicles</option>
                      {vehicles.map((v) => (
                        <option key={v.Vehicle_ID} value={v.Vehicle_ID}>
                          {v.Registration_No}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-theme">
                    <thead>
                      <tr>
                        <th>Sr.</th>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Description</th>
                        <th className="text-end">Income</th>
                        <th className="text-end">Expense</th>
                        <th className="text-end">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <Spinner animation="border" role="status" />
                          </td>
                        </tr>
                      ) : (
                        renderRows()
                      )}
                    </tbody>
                    {!loading && lines.length > 0 ? (
                      <tfoot>
                        <tr className="fw-bold">
                          <th colSpan="4" className="text-end">
                            TOTALS
                          </th>
                          <th className="text-end">Rs. {money(totals.income)}</th>
                          <th className="text-end">Rs. {money(totals.expense)}</th>
                          <th className="text-end">Rs. {money(totals.net)}</th>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BusAuditReport;
